import React from 'react';
import { AlertCircle } from 'lucide-react';

/**
 * ModernTextarea - Clean, Minimal Textarea Component
 * Consistent styling across the template builder
 */
const ModernTextarea = ({
  label,
  value,
  onChange,
  placeholder,
  required = false,
  rows = 4,
  className = "",
  description = "",
  validationRules = {},
  fieldPath = "",
  showValidation = true,
  validationErrors = {},
  setValidationErrors = () => {},
  disabled = false,
  maxLength = null
}) => {
  const error = validationErrors[fieldPath];
  const hasError = error && showValidation;
  const charCount = value ? value.length : 0;

  const validateField = (path, value, rules = {}) => {
    const errors = [];
    if (rules.required && (!value || value.toString().trim() === '')) {
      errors.push('This field is required');
    }
    if (rules.minLength && value && value.length < rules.minLength) {
      errors.push(`Minimum ${rules.minLength} characters required`);
    }
    if (rules.maxLength && value && value.length > rules.maxLength) {
      errors.push(`Maximum ${rules.maxLength} characters allowed`);
    }
    return errors.length > 0 ? errors[0] : null;
  };

  return (
    <div className={`space-y-1.5 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      {description && (
        <p className="text-xs text-gray-500">{description}</p>
      )}
      <div className="relative">
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => {
            if (fieldPath && validationRules && Object.keys(validationRules).length > 0) {
              const error = validateField(fieldPath, e.target.value, validationRules);
              if (error) {
                setValidationErrors(prev => ({ ...prev, [fieldPath]: error }));
              } else {
                setValidationErrors(prev => {
                  const newErrors = { ...prev };
                  delete newErrors[fieldPath];
                  return newErrors;
                });
              }
            }
          }}
          rows={rows}
          maxLength={maxLength}
          disabled={disabled}
          className={`
            w-full px-3 py-2.5 text-sm
            border rounded-lg
            transition-all duration-150
            placeholder:text-gray-400
            resize-none
            disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
            ${hasError
              ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-2 focus:ring-red-100'
              : 'border-gray-300 bg-white hover:border-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/10'
            }
            focus:outline-none
          `}
          placeholder={placeholder}
        />
        {hasError && (
          <div className="absolute top-2.5 right-3 pointer-events-none">
            <AlertCircle className="h-4 w-4 text-red-400" />
          </div>
        )}
      </div>
      <div className="flex items-center justify-between">
        {hasError ? (
          <p className="text-xs text-red-600 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {error}
          </p>
        ) : (
          <span />
        )}
        {maxLength && (
          <p className={`text-xs ${charCount > maxLength * 0.9 ? 'text-amber-600' : 'text-gray-400'}`}>
            {charCount}/{maxLength}
          </p>
        )}
      </div>
    </div>
  );
};

export default ModernTextarea;
